import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';

import { FormFieldDropdown } from '~/components/FormField';
import { useUserTypes } from '~settings/api/queries/userTypes';
import { UserTypeMap } from '~settings/constants/UserTypeMap';

import type { UserType } from '../../api/lib/schemas';
import { UserTypeInfoModal } from '../../modules/user/components/UserForm/components/UserTypeInfoModal';

interface SelectUserTypeProps
  extends Omit<
      ComponentProps<typeof FormFieldDropdown>,
      'label' | 'onChange' | 'options'
    >,
    Partial<Pick<ComponentProps<typeof FormFieldDropdown>, 'label'>> {
  includeAllOption?: boolean;
  onChange?: (
    value: string | string[],
    options: { label: string; value: string }[],
  ) => void;
  targetUserType?: keyof typeof UserTypeMap;
}

const mapRoles = (
  roles: readonly { id: string; name: string }[],
  t: (key: string) => string,
) =>
  roles
    .filter(({ name }) => Object.keys(UserTypeMap).includes(name))
    .map((role) => ({
      value: role.name,
      label: t(
        `userTypes.${UserTypeMap[role.name as keyof typeof UserTypeMap]}.name`,
      ),
    }));

export function SelectUserType({
  includeAllOption = false,
  multiselect = false,
  name,
  onChange,
  targetUserType,
  ...props
}: SelectUserTypeProps) {
  const { t } = useTranslation('settings');

  const { userTypes, isLoading } = useUserTypes({ targetUserType });

  const availableUserTypeDescriptions =
    userTypes?.data?.filter(
      (type) =>
        UserTypeMap[type.name as UserType] !== UserTypeMap.INTERNAL &&
        UserTypeMap[type.name as UserType] !== undefined,
    ) || [];
  const types = [
    ...(includeAllOption ? [{ value: 'all', label: t('all') }] : []),
    ...mapRoles(userTypes?.data || [], t),
  ];

  const handleChange = (value: string | string[]) => {
    onChange?.(value, types);
  };

  const count = multiselect ? 2 : 1;

  return (
    <FormFieldDropdown
      label={t('dropdowns.userType.title', { count })}
      placeholder={t('dropdowns.userType.placeholder', { count })}
      {...props}
      loading={isLoading}
      multiselect={multiselect}
      displaySelectedOptions={multiselect}
      name={name}
      onChange={handleChange}
      options={types}
      readOnly={isLoading || props.readOnly}
      labelAddon={
        <UserTypeInfoModal availableUserTypes={availableUserTypeDescriptions} />
      }
    />
  );
}

SelectUserType.displayName = 'SelectUserType';
